import { Dashboard, DashboardAvgTime, DashboardMonthData, DashboardOrders, DashboardQcStatusCount, DashboardPendingUser, DashboardQualityControls } from '../../domain/entities/dashboard.entity';

export interface DashboardMonthDataApi {
  year: number;
  month: number;
  count: number;
}

export interface DashboardQcStatusApi {
  status: number;
  count: number;
}

export interface DashboardPendingUserApi {
  userId: string;
  fullName: string;
  count: number;
}

export interface DashboardAvgTimeApi {
  days: number;
  hours: number;
  minutes: number;
}

export interface DashboardApiResponse {
  totalOrderCount: number;
  pendingOrderCount: number;
  finishedOrderCount: number;
  cancelledOrderCount: number;
  totalOrdersByMonth: DashboardMonthDataApi[];
  finishedOrdersByMonth: DashboardMonthDataApi[];
  qualityControlsByStatus: DashboardQcStatusApi[];
  qualityControlAverageSolvingDuration: DashboardAvgTimeApi | null;
  finishedQualityControlOkCount: number;
  finishedQualityControlKoCount: number;
  finishedQualityControlTotalCount: number;
  pendingTechnicalQualityControl: DashboardPendingUserApi[];
}

export class DashboardMapper {
  static toDomain(api: DashboardApiResponse): Dashboard {
    return {
      orders: DashboardMapper.mapOrders(api),
      qualityControls: DashboardMapper.mapQualityControls(api),
    };
  }

  private static mapOrders(api: DashboardApiResponse): DashboardOrders {
    return {
      totalOrders: api.totalOrderCount ?? 0,
      pendingOrders: api.pendingOrderCount ?? 0,
      cancelledOrders: api.cancelledOrderCount ?? 0,
      finalizedOrders: api.finishedOrderCount ?? 0,
      totalOrdersByMonth: (api.totalOrdersByMonth ?? []).map(DashboardMapper.mapMonthData),
      finalizedByMonth: (api.finishedOrdersByMonth ?? []).map(DashboardMapper.mapMonthData),
    };
  }

  private static mapMonthData(api: DashboardMonthDataApi): DashboardMonthData {
    return { year: api.year, month: api.month, count: api.count };
  }

  private static mapQualityControls(api: DashboardApiResponse): DashboardQualityControls {
    return {
      countByStatus: (api.qualityControlsByStatus ?? []).map((s): DashboardQcStatusCount => ({
        status: s.status,
        count: s.count,
      })),
      avgExecutionTime: api.qualityControlAverageSolvingDuration ?? null,
      aptoCount: api.finishedQualityControlOkCount ?? 0,
      notAptoCount: api.finishedQualityControlKoCount ?? 0,
      pendingByUser: (api.pendingTechnicalQualityControl ?? [])
        .slice(0, 6)
        .map((u): DashboardPendingUser => ({
          userId: u.userId,
          fullName: u.fullName,
          count: u.count,
        })),
    };
  }

  static formatAvgTime(avg: DashboardAvgTime | null): string {
    if (!avg) return '—';
    const parts: string[] = [];
    if (avg.days > 0) parts.push(`${avg.days} ${avg.days === 1 ? 'DÍA' : 'DÍAS'}`);
    if (avg.hours > 0) parts.push(`${avg.hours} ${avg.hours === 1 ? 'HORA' : 'HORAS'}`);
    if (avg.minutes > 0 && avg.days === 0) parts.push(`${avg.minutes} MIN`);
    return parts.length > 0 ? parts.join(' ') : '—';
  }
}
